import { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { Theme } from '@app/theme';
import { NotFound, useToast } from '@app/core';
import {
  ProspectActionEnum,
  QuoteInformation,
  useCreateDraftQuote,
  useBillingInformationsByOrga,
  useCreateProspect,
  useIsAdmin,
  useOrganizationCapabilities,
  useUpdateBillingMethod,
} from 'queries';
import { getLocale } from 'i18n';
import { QuoteLayout } from './QuoteLayout';
import {
  initialRecipient,
  QuoteSetupActions,
  QuoteStudySetup,
  QuoteSubjectSection,
  Recipient,
  RecipientSection,
  useQuoteOfferIds,
  useQuoteRecipient,
} from './components';

type Values = Partial<QuoteInformation>;

const initialValues: Values = {
  source: 'TANDEMZ',
  countryKeys: [],
};

const useStyles = createUseStyles((theme: Theme) => ({
  page: {
    maxWidth: theme.maxContentWidth.largeForm,
    margin: 'auto',
    paddingBottom: theme.marginBase * 20,
  },
  card: {
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.grayBorder}`,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 4,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 4,
  },
  block: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 1.5,
  },
  blockTitle: {
    ...theme.fonts.h4,
    color: theme.colors.inkPrimary,
    margin: 0,
  },
  setup: {
    marginTop: theme.marginBase * 5,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 3,
    paddingTop: theme.marginBase * 3,
  },
}));

export const CreateQuotePage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const toast = useToast();
  const [locale] = useState(() => getLocale() || 'en');
  const [recipient, setRecipient] = useState<Recipient>(initialRecipient);
  const [isStudy, setIsStudy] = useState(false);
  const { chosen: chosenRecipient, organizationId: recipientOrganizationId } =
    useQuoteRecipient(recipient);
  const { data: recipientCapabilities } = useOrganizationCapabilities(
    recipientOrganizationId,
  );
  const quoteOfferIds = useQuoteOfferIds(recipientCapabilities);

  const { data: recipientBillingInformations } = useBillingInformationsByOrga(
    recipientOrganizationId,
  );
  const { mutateAsync: createProspect } = useCreateProspect();
  const { mutateAsync: createQuote } = useCreateDraftQuote();
  const { mutateAsync: updateBillingMethod } = useUpdateBillingMethod(
    recipientBillingInformations?.id,
  );

  const submit = useCallback(
    async (values: Values) => {
      try {
        const prospect = recipient.fromOrganization
          ? undefined
          : await createProspect({
              firstName: recipient.firstName,
              lastName: recipient.lastName,
              email: recipient.email,
              phoneNumber: recipient.phoneNumber,
              companyName: recipient.companyName,
              locale,
              billingCountryCode: recipient.billingCountryCode,
              billingMethod: values.billingMethod,
              lastAction: ProspectActionEnum.SAVE_QUOTE,
            });

        if (
          recipient.fromOrganization &&
          values.billingMethod &&
          recipientBillingInformations &&
          !recipientBillingInformations.defaultStudyBillingMethod
        ) {
          await updateBillingMethod({ billingMethod: values.billingMethod });
        }

        const quote = await createQuote({
          prospectId: prospect?.id,
          organizationId: recipient.fromOrganization
            ? recipient.organizationId
            : undefined,
          clientId: recipient.fromOrganization
            ? recipient.clientId || undefined
            : undefined,
          billingMethod: values.billingMethod,
          billingCountry: recipient.fromOrganization
            ? undefined
            : values.billingCountry || recipient.billingCountryCode,
          ...(isStudy
            ? {
                offerIds: quoteOfferIds(values),
                countryKeys: values.countryKeys,
                priceTierId: values.priceThierId,
                studyType: values.studyType,
                studySubject: values.studySubject,
                format: values.format,
                duration: Number(values.duration) || 0,
                durationDisplayed: values.durationDisplayed,
                groupMeeting: values.groupMeeting,
                numberOfParticipantRequired:
                  Number(values.numberOfParticipantRequired) || 0,
              }
            : { duration: 0, numberOfParticipantRequired: 0 }),
        });

        navigate(`/quote/${quote.ref || quote.id}`);
      } catch (e) {
        toast.exception(e);
      }
    },
    [
      createProspect,
      createQuote,
      isStudy,
      locale,
      navigate,
      quoteOfferIds,
      recipient,
      recipientBillingInformations,
      toast,
      updateBillingMethod,
    ],
  );

  if (!isAdmin) {
    return <NotFound />;
  }

  return (
    <Formik initialValues={initialValues} onSubmit={submit}>
      <QuoteLayout
        navTitle={t('quote.create.title')}
        actions={<QuoteSetupActions disabled={!chosenRecipient} />}
      >
        <Form className={classes.page}>
          <div className={classes.card}>
            <div className={classes.block}>
              <h3 className={classes.blockTitle}>
                {t('quote.create.recipient')}
              </h3>
              <RecipientSection value={recipient} onChange={setRecipient} />
            </div>

            {chosenRecipient && (
              <div className={classes.block}>
                <h3 className={classes.blockTitle}>
                  {t('quote.create.subject')}
                </h3>
                <QuoteSubjectSection
                  isStudy={isStudy}
                  onIsStudyChange={setIsStudy}
                />
              </div>
            )}
          </div>

          <div className={classes.setup}>
            <QuoteStudySetup
              visible={chosenRecipient && isStudy}
              organizationId={recipientOrganizationId}
            />
          </div>

          <div className={classes.footer}>
            <QuoteSetupActions disabled={!chosenRecipient} />
          </div>
        </Form>
      </QuoteLayout>
    </Formik>
  );
};
